import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, Target } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant table modulaire des groupes d'annonces Google Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const AdGroupsTable = React.memo(({
  rawData,
  campaignsData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  selectedAccount = null,
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  selectedPeriod,
  customStartDate,
  customEndDate,
  token,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ad_groups'
}) => {
  // États locaux pour cette table
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [previousAdGroupsData, setPreviousAdGroupsData] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('ad_groups', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);



  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (adGroup, prevAdGroup, hasComparison) => (
        <>
          {formatNumber(adGroup.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(adGroup.impressions || 0, prevAdGroup.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (adGroup, prevAdGroup, hasComparison, ctr, prevCtr) => (
        <>
          {formatNumber(ctr, 2)}%
          {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (adGroup, prevAdGroup, hasComparison) => (
        <>
          {formatNumber(adGroup.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(adGroup.clicks || 0, prevAdGroup.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (adGroup, prevAdGroup, hasComparison, ctr, prevCtr, cpc, prevCpc) => (
        <>
          {formatNumber(cpc, 2)}€
          {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cpc, 2)}€</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (adGroup, prevAdGroup, hasComparison) => (
        <>
          {formatNumber(adGroup.cost || 0, 2)}€
          {hasComparison && renderVariation(calculateVariation(adGroup.cost || 0, prevAdGroup.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cost, 2)}€</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (adGroup, prevAdGroup, hasComparison, ctr, prevCtr, cpc, prevCpc, conversionRate, prevConversionRate) => (
        <>
          {formatNumber(conversionRate, 2)}%
          {hasComparison && renderVariation(calculateVariation(conversionRate, prevConversionRate))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (adGroup, prevAdGroup, hasComparison) => (
        <>
          {formatNumber(adGroup.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(adGroup.conversions || 0, prevAdGroup.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (cpa) => getCpaClassName(cpa, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (adGroup, prevAdGroup, hasComparison, ctr, prevCtr, cpc, prevCpc, conversionRate, prevConversionRate, cpa, prevCpa) => (
        <>
          {formatNumber(cpa, 2)}€
          {hasComparison && renderVariation(calculateVariation(cpa, prevCpa), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cpa, 2)}€</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (adGroup, prevAdGroup, hasComparison) => (
        <>
          {formatNumber(adGroup.conversionsValue || 0, 2)}€
          {hasComparison && renderVariation(calculateVariation(adGroup.conversionsValue || 0, prevAdGroup.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionsValue, 2)}€</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (cos) => getCosClassName(cos, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (adGroup, prevAdGroup, hasComparison, ctr, prevCtr, cpc, prevCpc, conversionRate, prevConversionRate, cpa, prevCpa, cos, prevCos) => (
        <>
          {formatNumber(cos, 1)}%
          {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (adGroup, prevAdGroup, hasComparison) => {
        const panierMoyen = (adGroup.conversions || 0) > 0 ? (adGroup.conversionsValue || 0) / adGroup.conversions : 0;
        const prevPanierMoyen = prevAdGroup && (prevAdGroup.conversions || 0) > 0 ? (prevAdGroup.conversionsValue || 0) / prevAdGroup.conversions : 0;
        return (
          <>
            €{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>€{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (adGroup, prevAdGroup, hasComparison) => {
        const roas = (adGroup.cost || 0) > 0 ? ((adGroup.conversionsValue || 0) / adGroup.cost) * 100 : 0;
        const prevRoas = prevAdGroup && (prevAdGroup.cost || 0) > 0 ? ((prevAdGroup.conversionsValue || 0) / prevAdGroup.cost) * 100 : 0;
        return (
          <>
            {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (adGroup, prevAdGroup, hasComparison) => {
        const hasData = (adGroup.cost || 0) > 0 && (adGroup.conversionsValue || 0) > 0;
        const roi = hasData ? ((adGroup.conversionsValue || 0) - adGroup.cost) / adGroup.cost : 0;
        const prevHasData = (prevAdGroup?.cost || 0) > 0 && (prevAdGroup?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((prevAdGroup.conversionsValue || 0) - prevAdGroup.cost) / prevAdGroup.cost : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const hasData = (totals.cost || 0) > 0 && (totals.conversionsValue || 0) > 0;
        const roi = hasData ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevHasData = (previousTotals?.cost || 0) > 0 && (previousTotals?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);



  // Extraction des données de groupes d'annonces (memoized)
  const adGroupsData = useMemo(() => {
    if (!rawData) return [];

    // Chercher dans adGroupStats et adGroups (compatibilité)
    const accountData = Object.values(rawData).find(data => data.adGroupStats || data.adGroups);
    let allAdGroups = accountData?.adGroupStats || accountData?.adGroups || [];

    // Appliquer le filtre par campagne si une campagne est sélectionnée
    if (selectedCampaignFilter) {
      allAdGroups = allAdGroups.filter(adGroup => {
        const match = adGroup.campaignId === selectedCampaignFilter.id ||
                     adGroup.campaignId === selectedCampaignFilter.campaignId ||
                     adGroup.campaignId?.toString() === selectedCampaignFilter.id?.toString() ||
                     adGroup.campaignName === selectedCampaignFilter.name;

        return match;
      });
    }

    // Appliquer le filtre par type de campagne si un type est sélectionné
    if (selectedCampaignTypeFilter) {
      console.log('🎯 [AD-GROUPS] Filtrage par type de campagne:', selectedCampaignTypeFilter);

      // Utiliser les campagnes passées en prop depuis App.js
      const campaigns = campaignsData || [];

      console.log('🎯 [AD-GROUPS] Nombre total de campagnes disponibles:', campaigns.length);
      console.log('🎯 [AD-GROUPS] Première campagne exemple:', campaigns[0]);

      // Trouver les IDs des campagnes qui correspondent au type sélectionné
      const matchingCampaignIds = campaigns
        .filter(campaign => {
          const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
          const matches = campaignType === selectedCampaignTypeFilter.type ||
                         String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                         campaignType === selectedCampaignTypeFilter.name ||
                         String(campaignType) === String(selectedCampaignTypeFilter.name);

          if (matches) {
            console.log('🎯 [AD-GROUPS] Campagne correspondante:', campaign.name, 'ID:', campaign.id, 'Type:', campaignType);
          }

          return matches;
        })
        .map(campaign => campaign.id || campaign.campaignId);

      console.log('🎯 [AD-GROUPS] IDs des campagnes correspondantes:', matchingCampaignIds);

      // Filtrer les groupes d'annonces qui appartiennent à ces campagnes
      allAdGroups = allAdGroups.filter(adGroup => {
        const adGroupCampaignId = adGroup.campaignId || adGroup.campaign_id;
        const matches = matchingCampaignIds.includes(adGroupCampaignId) ||
                       matchingCampaignIds.includes(String(adGroupCampaignId));

        if (matches) {
          console.log('🎯 [AD-GROUPS] Groupe d\'annonces correspondant:', adGroup.name, 'Campaign ID:', adGroupCampaignId);
        }

        return matches;
      });

      console.log('🎯 [AD-GROUPS] Groupes d\'annonces après filtrage par type:', allAdGroups.length);
    }

    // Appliquer le filtre par labels si des labels sont sélectionnés
    if (selectedLabelsFilter.length > 0) {
      console.log('🏷️ [AD-GROUPS] Filtrage par labels:', selectedLabelsFilter);
      allAdGroups = allAdGroups.filter(adGroup => {
        const adGroupCampaignId = adGroup.campaignId || adGroup.campaign_id;
        return campaignMatchesLabelsFilter(adGroupCampaignId);
      });
      console.log('🏷️ [AD-GROUPS] Groupes d\'annonces après filtrage par labels:', allAdGroups.length);
    }

    return allAdGroups;
  }, [rawData, campaignsData, selectedCampaignFilter, selectedCampaignTypeFilter, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Charger les données de la période précédente si comparaison activée
  useEffect(() => {
    if (!isVisible) return;

    if (!compareEnabled || !comparisonType || !selectedAccount?.id || !token) {
      setPreviousAdGroupsData([]);
      return;
    }

    const loadPreviousData = async () => {
      setLoadingPrevious(true);

      // Calculer les dates de la période actuelle
      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;

      // Si pas de dates custom, les calculer à partir de selectedPeriod
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      console.log(`🎯 [AD-GROUPS] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      try {
        const response = await axios.post(`${config.API_URL}/api/dashboard/components/ad-groups`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data?.success) {
          // L'endpoint retourne { success, data, rawData }
          // rawData contient { [sourceId]: { adGroups: [...] } }
          const rawData = response.data.rawData || {};
          const accountData = Object.values(rawData)[0];
          const prevAdGroups = accountData?.adGroups || [];
          console.log(`🎯 [AD-GROUPS] ${prevAdGroups.length} groupes d'annonces période précédente`);
          setPreviousAdGroupsData(prevAdGroups);
        }
      } catch (error) {
        console.warn(`⚠️ [AD-GROUPS] Erreur chargement période précédente:`, error.message);
        setPreviousAdGroupsData([]);
      } finally {
        setLoadingPrevious(false);
      }
    };

    loadPreviousData();
  }, [isVisible, compareEnabled, comparisonType, customStartDate, customEndDate, selectedAccount?.id, token, selectedPeriod]);

  // Fonction de tri memoized
  const sortedAdGroups = useMemo(() => {
    if (!adGroupsData.length) return [];

    const sorted = [...adGroupsData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' ?
          aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      // Pour les valeurs numériques
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [adGroupsData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!adGroupsData.length) return {};

    const baseTotals = adGroupsData.reduce((acc, adGroup) => ({
      impressions: acc.impressions + (adGroup.impressions || 0),
      clicks: acc.clicks + (adGroup.clicks || 0),
      cost: acc.cost + (adGroup.cost || 0),
      conversions: acc.conversions + (adGroup.conversions || 0),
      conversionsValue: acc.conversionsValue + (adGroup.conversionsValue || 0),
      ...cumulerVideo(acc, adGroup)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [adGroupsData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousAdGroupsData.length) return null;

    const baseTotals = previousAdGroupsData.reduce((acc, adGroup) => ({
      impressions: acc.impressions + (adGroup.impressions || 0),
      clicks: acc.clicks + (adGroup.clicks || 0),
      cost: acc.cost + (adGroup.cost || 0),
      conversions: acc.conversions + (adGroup.conversions || 0),
      conversionsValue: acc.conversionsValue + (adGroup.conversionsValue || 0),
      ...cumulerVideo(acc, adGroup)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousAdGroupsData]);

  // Configuration de pagination
  const itemsPerPage = 10;
  const totalPages = Math.ceil(sortedAdGroups.length / itemsPerPage);

  // Données paginées memoized
  const paginatedAdGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedAdGroups.slice(startIndex, endIndex);
  }, [sortedAdGroups, currentPage, itemsPerPage]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Réinitialiser à la première page
  }, []);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Debug logs (memoized)

  // Ne pas rendre si pas de données
  if (!adGroupsData.length) return null;

  // Vérifier s'il y a des données valides (pas seulement des données avec des valeurs nulles/zéros)
  const hasValidData = adGroupsData.some(adGroup =>
    (adGroup?.impressions > 0) ||
    (adGroup?.clicks > 0) ||
    (adGroup?.cost > 0) ||
    (adGroup?.conversions > 0)
  );

  if (!hasValidData) return null;

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Target size={16} />&nbsp;Groupes d'annonces</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Groupes d'annonces"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Indicateur de chargement des données de comparaison */}
          {compareEnabled && loadingPrevious && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}
          {(!compareEnabled || !loadingPrevious) && (
          <>
          <div className="campaigns-table-wrapper">
            <table
              key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
              className="campaigns-table"
              style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Groupe d'annonces {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                        {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {paginatedAdGroups.map((adGroup, index) => {
                  // Trouver la ligne correspondante dans les données de la période précédente
                  const prevAdGroup = compareEnabled && previousAdGroupsData.length > 0
                    ? previousAdGroupsData.find(p =>
                        p.id === adGroup.id ||
                        p.adGroupId === adGroup.adGroupId ||
                        (p.name === adGroup.name && p.campaignId === adGroup.campaignId)
                      )
                    : null;
                  const hasComparison = !!prevAdGroup;

                  // Calculer les métriques dérivées pour la période actuelle
                  const ctr = adGroup.ctr || (adGroup.impressions > 0 ? (adGroup.clicks / adGroup.impressions * 100) : 0);
                  const cpc = adGroup.cpc || (adGroup.clicks > 0 ? (adGroup.cost / adGroup.clicks) : 0);
                  const conversionRate = adGroup.conversionRate || (adGroup.clicks > 0 ? (adGroup.conversions / adGroup.clicks * 100) : 0);
                  const cpa = adGroup.cpa || (adGroup.conversions > 0 ? (adGroup.cost / adGroup.conversions) : 0);
                  const cos = adGroup.cos || (adGroup.conversionsValue > 0 ? (adGroup.cost / adGroup.conversionsValue * 100) : 0);

                  // Calculer les métriques dérivées pour la période précédente
                  const prevCtr = prevAdGroup ? (prevAdGroup.ctr || (prevAdGroup.impressions > 0 ? (prevAdGroup.clicks / prevAdGroup.impressions * 100) : 0)) : 0;
                  const prevCpc = prevAdGroup ? (prevAdGroup.cpc || (prevAdGroup.clicks > 0 ? (prevAdGroup.cost / prevAdGroup.clicks) : 0)) : 0;
                  const prevConversionRate = prevAdGroup ? (prevAdGroup.conversionRate || (prevAdGroup.clicks > 0 ? (prevAdGroup.conversions / prevAdGroup.clicks * 100) : 0)) : 0;
                  const prevCpa = prevAdGroup ? (prevAdGroup.cpa || (prevAdGroup.conversions > 0 ? (prevAdGroup.cost / prevAdGroup.conversions) : 0)) : 0;
                  const prevCos = prevAdGroup ? (prevAdGroup.cos || (prevAdGroup.conversionsValue > 0 ? (prevAdGroup.cost / prevAdGroup.conversionsValue * 100) : 0)) : 0;

                  return (
                    <tr key={index}>
                      <td className="campaign-name" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {adGroup.isDelivering !== undefined && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: adGroup.isDelivering ? '#22c55e' : '#ef4444',
                                marginRight: '8px',
                                flexShrink: 0
                              }}
                              title={adGroup.isDelivering ? 'Actif' : 'Inactif'}
                            />
                          )}
                          <span>{adGroup.name || adGroup.adGroupName || '-'}</span>
                        </div>
                        {adGroup.campaignName && (
                          <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', marginLeft: adGroup.isDelivering !== undefined ? '16px' : '0' }}>
                            {adGroup.campaignName}
                          </div>
                        )}
                      </td>
                      {/* Rendu dynamique des cellules selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        const className = config.getClassName
                          ? `numeric ${config.getClassName(metricKey === 'cpa' ? cpa : cos)}`
                          : 'numeric';
                        return (
                          <td key={metricKey} className={className}>
                            {config.renderCell(adGroup, prevAdGroup || {}, hasComparison, ctr, prevCtr, cpc, prevCpc, conversionRate, prevConversionRate, cpa, prevCpa, cos, prevCos)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td className="campaign-name"><strong>TOTAL</strong></td>
                  {/* Rendu dynamique des totaux selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    const className = config.getTotalClassName
                      ? `numeric ${config.getTotalClassName(totals)}`
                      : 'numeric';
                    return (
                      <td key={metricKey} className={className}>
                        {config.renderTotal(totals, previousTotals)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedAdGroups.length)} sur {sortedAdGroups.length} groupes d'annonces
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
          </>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ad_groups"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Groupes d'annonces"
      />
    </div>
  );
});

AdGroupsTable.displayName = 'AdGroupsTable';

export default AdGroupsTable;
